import { type PlatformType } from '@cart-services/hooks/fp-b2b/store-map'
import { useFpB2bCartService } from '@cart-services/hooks/fp-b2b/use-fp-b2b-cart-service'
import { type CartItem, type PartialCartItem } from '@cart-services/types'
import { useDialog } from '@dni-hooks/useDialog'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { Button } from '@pets-ui/Button/Button'
import { Dialog } from '@pets-ui/Dialog/Dialog'
import { DialogActions } from '@pets-ui/DialogActions/DialogActions'
import { DialogContent } from '@pets-ui/DialogContent/DialogContent'
import { DialogHeader } from '@pets-ui/DialogHeader/DialogHeader'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { Trans, useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom-v5'

export const DIALOG_ADD_TO_CART_ERROR = 'DIALOG_ADD_TO_CART_ERROR'
export interface DialogAddToCartErrorProps {
  campaignId?: number
  items: PartialCartItem[]
  basePath: string
  platform: PlatformType
}

const DialogAddToCartErrorBody = ({ handleNewItemsOnly, handleProceedToCheckout, onClose }: {
  handleNewItemsOnly: () => void
  handleProceedToCheckout: () => void
  onClose: () => void
}) => {
  const { t } = useTranslation()

  return (
    <>
      <DialogHeader align="center" title={t('wpOrder.checkoutSeparate', { defaultValue: '無法合併加車' })} onClose={onClose} />
      <DialogContent>
        <Text align="center" color={colors.grey800}>
          <Trans
            i18nKey="wpOrder.checkoutSeparateText"
            components={{
              Br: <div />,
            }}
          />
        </Text>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleNewItemsOnly}
          fullWidth
          variant="outlined"
          color="grey"
          size="large"
          whiteSpace="nowrap"
        >
          {t('wpOrder.newItemsOnly', { defaultValue: '清空並加入新商品' })}
        </Button>
        <Button
          onClick={handleProceedToCheckout}
          fullWidth
          size="large"
          whiteSpace="nowrap"
        >
          {t('wpOrder.proceedToCheckout', { defaultValue: '先結帳再購買' })}
        </Button>
      </DialogActions>
    </>
  )
}

const DialogAddToCartErrorComponent = () => {
  const { t } = useTranslation()

  const { closeDialog, campaignId, items, basePath, platform } = useDialog<DialogAddToCartErrorProps>(DIALOG_ADD_TO_CART_ERROR)
  const { updateCartItems } = useFpB2bCartService(campaignId ?? null, platform)
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()

  const history = useHistory()

  const showSnackbar = () => {
    openSnackbar({
      message: t('wpOrder.addedSuccessfully', { defaultValue: '訂單商品已加入購物車！' }),
      severity: 'success',
      action: (
        <Button
          variant="text"
          color="blue"
          onClick={() => history.push(`${basePath}/cart`)}
        >
          {t('wpOrder.checkout', { defaultValue: '前往結帳' })}
        </Button>
      ),
    })
  }

  const handleNewItemsOnly = useCallbackEvent(() => {
    updateCartItems(items as CartItem[])
    closeDialog()
    showSnackbar()
  })

  const handleProceedToCheckout = useCallbackEvent(() => {
    history.push(`${basePath}/cart`)
    closeDialog()
  })

  return (
    <DialogAddToCartErrorBody
      onClose={closeDialog}
      handleNewItemsOnly={handleNewItemsOnly}
      handleProceedToCheckout={handleProceedToCheckout}
    />
  )
}

export const DialogAddToCartError = () => {
  const { open, closeDialog } = useDialog<DialogAddToCartErrorProps>(DIALOG_ADD_TO_CART_ERROR)

  return (
    <Dialog onClose={closeDialog} open={open} maxWidth={320} fullWidth>
      {open && <DialogAddToCartErrorComponent />}
    </Dialog>
  )
}
